import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Collapse,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';

function InvoiceDetailsModal({ open, onClose, invoiceRow, invoiceItems, props }) {
  const [invoiceDetailData, setInvoiceDetailData] = React.useState(null);
  const [issueInvoiceData, setIssueInvoiceData] = React.useState(null);
  const [invoiceDetailLoading, setInvoiceDetailLoading] = React.useState(false);
  const [issueInvoiceLoading, setIssueInvoiceLoading] = React.useState(false);
  const [expandedInvoices, setExpandedInvoices] = React.useState({});

  React.useEffect(() => {
    if (open && invoiceRow?.id) {
      setInvoiceDetailData(null);
      setIssueInvoiceData(null);
      setInvoiceDetailLoading(true);
      setIssueInvoiceLoading(false);
      setExpandedInvoices({});
      
      // Fetch detailed invoice data including related invoices
      getRequest(`${GET_URL.issued_invoices.api}${invoiceRow.id}/`, {}, props)
        .then((response) => {
          setInvoiceDetailLoading(false);
          if (response && response.status === 200) {
            const data = response.data?.data || response.data;
            setInvoiceDetailData(data);
            
            // If invoice has issue_invoice_id, fetch issue invoice details
            const issueInvoiceId = data.issue_invoice || data.issue_invoice_id;
            if (issueInvoiceId) {
              setIssueInvoiceLoading(true);
              getRequest(`${GET_URL.issued_invoices.api}${issueInvoiceId}/`, {}, props)
                .then((issueResponse) => {
                  setIssueInvoiceLoading(false);
                  if (issueResponse && issueResponse.status === 200) {
                    const issueData = issueResponse.data?.data || issueResponse.data;
                    setIssueInvoiceData(issueData);
                  }
                })
                .catch(() => {
                  setIssueInvoiceLoading(false);
                });
            }
          }
        })
        .catch(() => {
          setInvoiceDetailLoading(false);
        });
    }
  }, [open, invoiceRow?.id, props]);

  const handleClose = () => {
    setInvoiceDetailData(null);
    setIssueInvoiceData(null);
    setInvoiceDetailLoading(false);
    setIssueInvoiceLoading(false);
    setExpandedInvoices({});
    onClose();
  };

  const toggleInvoiceExpansion = (invoiceId) => {
    setExpandedInvoices((prev) => ({
      ...prev,
      [invoiceId]: !prev[invoiceId],
    }));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Invoice Details - {invoiceRow?.invoice_number}</DialogTitle>
      <DialogContent>
        {invoiceRow && (
          <Box mb={2} p={2} bgcolor="#f0f4ff" borderRadius={1} border={1} borderColor="grey.300">
            <Typography variant="subtitle2" fontWeight="bold">
              Summary
            </Typography>
            <Box display="flex" gap={3} flexWrap="wrap">
              <Typography variant="caption" color="textSecondary">
                Company: {invoiceRow.company_name || invoiceRow.name || '-'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Issue: {invoiceRow.issue_date ?? '-'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Due: {invoiceRow.due_date ?? '-'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Status: {invoiceRow.status ?? '-'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Tax Amt: {invoiceRow.tax_amount ?? '-'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Total Excl Tax: {invoiceRow.total_without_tax ?? '-'}
              </Typography>
              <Typography variant="caption" color="primary" style={{ fontWeight: 'bold' }}>
                Total: {invoiceRow.total_amount ?? '-'}
              </Typography>
            </Box>
          </Box>
        )}

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#4680FF', fontSize: 16 }}>
          Issue Invoice Items
        </Typography>
        {(!invoiceItems || invoiceItems.length === 0) ? (
          <Typography variant="body2" color="textSecondary">
            No items
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item Name</TableCell>
                <TableCell>Item Count</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Tax %</TableCell>
                <TableCell align="right">Tax Amount</TableCell>
                <TableCell>HSN/SAC</TableCell>
                <TableCell align="right">Total Excl Tax</TableCell>
                <TableCell align="right">Discount Amt</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoiceItems.map((it, idx) => (
                <TableRow key={`inv-item-${idx}`}>
                  <TableCell>{it.item_name ?? '-'}</TableCell>
                  <TableCell>{it.item_count ?? '-'}</TableCell>
                  <TableCell align="right">{it.amount ?? '-'}</TableCell>
                  <TableCell align="right">{it.tax_percent ?? '-'}</TableCell>
                  <TableCell align="right">{it.tax_amount ?? '-'}</TableCell>
                  <TableCell>{it.hsn_sac ?? ''}</TableCell>
                  <TableCell align="right">{it.total_without_tax ?? '-'}</TableCell>
                  <TableCell align="right">{it.discount_amount ?? '-'}</TableCell>
                  <TableCell align="right">{it.total ?? '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Issue Invoice Details Section - Show below Invoice Items and above Payment Details */}
        {issueInvoiceLoading ? (
          <Box mt={3}>
            <Typography variant="body2" color="textSecondary">
              Loading issue invoice details...
            </Typography>
          </Box>
        ) : issueInvoiceData ? (
          <Box mt={3}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#4680FF', fontSize: 16 }}>
              Issue Invoice Details
            </Typography>
            <Box p={2} bgcolor="#f9f9f9" borderRadius={1} border={1} borderColor="grey.300">
              <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" gap={3} flexWrap="wrap">
                  <Typography variant="body2">
                    <strong>Invoice Number:</strong> {issueInvoiceData.invoice_number ?? '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Issue Date:</strong> {issueInvoiceData.issue_date ?? '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Due Date:</strong> {issueInvoiceData.due_date ?? '-'}
                  </Typography>
                </Box>
                <Box display="flex" gap={3} flexWrap="wrap">
                  <Typography variant="body2">
                    <strong>Status:</strong> {issueInvoiceData.status ?? '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total Without Tax:</strong> {issueInvoiceData.total_without_tax ?? '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Tax Amount:</strong> {issueInvoiceData.tax_amount ?? '-'}
                  </Typography>
                  <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                    <strong>Total Amount:</strong> {issueInvoiceData.total_amount ?? '-'}
                  </Typography>
                </Box>
                {issueInvoiceData.notes && (
                  <Box mt={1}>
                    <Typography variant="body2">
                      <strong>Notes:</strong> {issueInvoiceData.notes}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        ) : null}

        {invoiceDetailLoading ? (
          <Box mt={3}>
            <Typography variant="body2" color="textSecondary">
              Loading invoices...
            </Typography>
          </Box>
        ) : invoiceDetailData?.invoices && invoiceDetailData.invoices.length > 0 ? (
          <>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3, mb: 1, color: '#4680FF', fontSize: 16 }}>
              Invoices
            </Typography>
            {invoiceDetailData.invoices.map((invoice) => {
              const isExpanded = expandedInvoices[invoice.id] || false;
              return (
                <Box key={`invoice-${invoice.id}`} mb={2}>
                  <Box
                    p={1.5}
                    bgcolor="#f5f5f5"
                    borderRadius={1}
                    border={1}
                    borderColor="grey.300"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => toggleInvoiceExpansion(invoice.id)}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box flex={1}>
                        <Typography variant="body2" fontWeight="bold">
                          Invoice: {invoice.invoice_number}
                        </Typography>
                        <Box display="flex" gap={2} flexWrap="wrap" mt={0.5}>
                          <Typography variant="caption" color="textSecondary">
                            Issue Date: {invoice.issue_date ?? '-'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Due Date: {invoice.due_date ?? '-'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Status: {invoice.status ?? '-'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            GST Applicable: {invoice.is_gst_applicable ? 'Yes' : 'No'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Total: {invoice.total_amount ?? '-'}
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleInvoiceExpansion(invoice.id);
                        }}
                      >
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </Box>
                  <Collapse in={isExpanded}>
                    <Box mt={1}>
                      <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                        Invoice Items
                      </Typography>
                      {invoice.items && invoice.items.length > 0 ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Item Name</TableCell>
                              <TableCell>Item Count</TableCell>
                              <TableCell align="right">Amount</TableCell>
                              <TableCell align="right">Tax %</TableCell>
                              <TableCell align="right">Tax Amount</TableCell>
                              <TableCell>HSN/SAC</TableCell>
                              <TableCell align="right">Total Excl Tax</TableCell>
                              <TableCell align="right">Discount Amt</TableCell>
                              <TableCell align="right">Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {invoice.items.map((item, itemIdx) => (
                              <TableRow key={`invoice-item-${invoice.id}-${itemIdx}`}>
                                <TableCell>{item.item_name ?? '-'}</TableCell>
                                <TableCell>{item.item_count ?? '-'}</TableCell>
                                <TableCell align="right">{item.amount ?? '-'}</TableCell>
                                <TableCell align="right">{item.tax_percent ?? '-'}</TableCell>
                                <TableCell align="right">{item.tax_amount ?? '-'}</TableCell>
                                <TableCell>{item.hsn_sac ?? ''}</TableCell>
                                <TableCell align="right">{item.total_without_tax ?? '-'}</TableCell>
                                <TableCell align="right">{item.discount_amount ?? '-'}</TableCell>
                                <TableCell align="right">{item.total ?? '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No items
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default InvoiceDetailsModal;

